import { useEffect, useState } from "react";
import * as devicesApi from "../../api/devices";
import { TipoPossessoDevice, TIPO_POSSESSO_LABELS } from "../../api/devices";
import { useAuth } from "../../context/AuthContext";

/**
 * "Questa matricola non risulta nel parco: la censisco?"
 *
 * ## Il difetto che questo blocco chiude
 *
 * Salvare un'installazione (o un'attivazione) con una matricola scritta a mano
 * non produceva NIENTE nel parco: il record restava orfano, `/devices` diceva
 * "Nessun dispositivo" e la scheda della sede "DISPOSITIVI: 0" mentre
 * l'installazione esisteva. Il testo della matricola non era mai stato tradotto
 * nel pezzo fisico che nomina.
 *
 * ## Perché una spunta e non un automatismo
 *
 * Censire in silenzio avrebbe chiuso il difetto con una riga in meno, ma la
 * matricola NON è rinominabile — la PATCH del device la rifiuta apposta,
 * perché è l'identificativo stampigliato sulla macchina e ci punta tutto lo
 * storico. Un refuso di battitura censito senza che nessuno lo veda diventa un
 * pezzo fantasma che si toglie solo cancellandolo. Qui invece la matricola
 * normalizzata si legge PRIMA di salvare, ed è il momento giusto per accorgersi
 * che si è scritto "UB12004" invece di "UB120004".
 *
 * ## Chi lo vede
 *
 * Solo chi ha `devices:create`. Censire il parco da un'installazione è la
 * stessa azione che censirlo da `/devices`, e il permesso non cambia perché
 * cambia la porta da cui si entra: `installazioni:create` e
 * `attivazioni:create` includono il Centro Assistenza, `devices:create` no.
 * La stessa regola è applicata anche nella rotta — questa è l'anticipazione in
 * interfaccia, non il controllo vero. Le due copie vanno tenute identiche.
 */
export function CensimentoDevice({
  matricola,
  deviceId,
  censisci,
  onCensisciChange,
  tipoPossesso,
  onTipoPossessoChange,
}: {
  /** Il testo scritto nel campo matricola. */
  matricola: string;
  /**
   * Device già scelto dall'autocomplete ("" = nessuno). Quando c'è, non c'è
   * niente da censire e il blocco sparisce senza nemmeno interrogare il
   * backend.
   */
  deviceId: string;
  censisci: boolean;
  onCensisciChange: (v: boolean) => void;
  /** "" = non specificato, che resta NULL a database. Vedi sotto. */
  tipoPossesso: TipoPossessoDevice | "";
  onTipoPossessoChange: (v: TipoPossessoDevice | "") => void;
}) {
  const { hasPermission } = useAuth();
  const puoCensire = hasPermission("devices:create");

  /** null = non ancora interrogato per questo testo. */
  const [esito, setEsito] = useState<devicesApi.EsitoMatricola | null>(null);

  const testo = matricola.trim();

  useEffect(() => {
    // Niente permesso, niente matricola, o device già scelto: nessuna domanda
    // da fare. Il device scelto dalla tendina esiste per costruzione.
    if (!puoCensire || !testo || deviceId) {
      setEsito(null);
      return;
    }

    // Debounce come negli altri autocomplete: il campo si interroga mentre si
    // scrive, e senza attesa partirebbe una richiesta per carattere.
    let annullato = false;
    const t = setTimeout(() => {
      devicesApi
        .cercaPerMatricola(testo)
        .then((r) => {
          if (!annullato) setEsito(r);
        })
        // La verifica è un aiuto: se non risponde, il campo resta com'era e il
        // salvataggio funziona come prima. Proporre il censimento quando non si
        // sa se la matricola esista significherebbe proporre un doppione.
        .catch(() => {
          if (!annullato) setEsito(null);
        });
    }, 350);

    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [testo, deviceId, puoCensire]);

  // La spunta non deve sopravvivere alla condizione che l'ha resa possibile:
  // se si corregge la matricola in una già censita, o si sceglie un device
  // dalla tendina, restare spuntata censirebbe qualcosa che nessuno ha più
  // chiesto — e l'utente non vedrebbe nemmeno più il riquadro.
  const daCensire = esito !== null && !esito.trovato;
  useEffect(() => {
    if (!daCensire && censisci) onCensisciChange(false);
  }, [daCensire, censisci, onCensisciChange]);

  if (!daCensire) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm text-ink">
        La matricola <strong>{esito.matricola}</strong> non risulta fra i dispositivi censiti.
      </p>
      {/* La matricola normalizzata, non quella digitata: è la forma con cui
          verrà salvata, ed è qui che si vede un refuso o uno spazio di troppo
          prima che diventi un pezzo che non si può più rinominare. */}
      <p className="text-xs text-ink/60">
        Senza censirla il record resta salvato ma non collegato a nessun dispositivo: non comparirà in Wink On Cash né
        sulla scheda della sede.
      </p>

      <label className="flex items-start gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-line"
          checked={censisci}
          onChange={(e) => onCensisciChange(e.target.checked)}
        />
        <span>
          Censisci il dispositivo <strong>{esito.matricola}</strong> al salvataggio, collegandolo al cliente e alla sede
          di questo record.
        </span>
      </label>

      {censisci && (
        <div className="flex flex-col gap-1.5 pl-6">
          <label className="text-xs text-ink/60">Possesso</label>
          <select
            className="h-9 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            value={tipoPossesso}
            onChange={(e) => onTipoPossessoChange(e.target.value as TipoPossessoDevice | "")}
          >
            {/* Vuoto e non un valore di comodo: il possesso è nullable a schema
                proprio perché "non noto" è diverso da qualunque risposta, e un
                default scritto qui diventerebbe indistinguibile da un dato
                verificato da qualcuno. Si compila dopo, dalla scheda del pezzo. */}
            <option value="">Non specificato</option>
            {(Object.keys(TIPO_POSSESSO_LABELS) as TipoPossessoDevice[]).map((p) => (
              <option key={p} value={p}>
                {TIPO_POSSESSO_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
